import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ChangePasswordModal from './components/ChangePasswordModal';
import ChangeUsernameModal from './components/ChangeUsernameModal';
import styles from './ProfileAccount.module.css';

const MENU_ITEMS = [
  {
    key: 'avatar',
    icon: 'image',
    iconColor: 'blue',
    title: '上传头像',
    desc: '更换您的个性化展示图片',
    action: 'navigate',
    path: '/profile/account/avatar',
  },
  {
    key: 'username',
    icon: 'edit',
    iconColor: 'amber',
    title: '修改用户名',
    desc: '设置一个独特的昵称',
    action: 'modal',
  },
  {
    key: 'password',
    icon: 'shield',
    iconColor: 'emerald',
    title: '修改密码',
    desc: '定期更新密码以保证账户安全',
    action: 'modal',
  },
  {
    key: 'payment',
    icon: 'receipt_long',
    iconColor: 'indigo',
    title: '支付记录',
    desc: '查看历史订阅与充值订单',
    action: 'navigate',
    path: '/payment/history',
  },
];

const STATUS_MAP = {
  REGISTERED: '已注册',
  REWARD_ISSUED: '已奖励',
  FAILED: '失败',
};

const ProfileAccount = () => {
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeUsername, setShowChangeUsername] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [pdfQuota, setPdfQuota] = useState(null);
  const [pdfUnlimited, setPdfUnlimited] = useState(false);

  useEffect(() => {
    fetchInviteRecords();
    fetchPdfQuota();
  }, []);

  const fetchInviteRecords = async () => {
    try {
      setInviteLoading(true);
      const { data } = await api.get('/auth/my-invite-records');
      setInviteData(data);
    } catch (err) {
      console.error('获取邀请记录失败:', err);
    } finally {
      setInviteLoading(false);
    }
  };

  const fetchPdfQuota = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setPdfUnlimited(data.pdf_unlimited === true);
      setPdfQuota(data.pdf_quota ?? 0);
    } catch (err) {
      console.error('获取PDF额度失败:', err);
    }
  };

  const handleCopyCode = async () => {
    if (!inviteData?.invite_code) return;
    try {
      await navigator.clipboard.writeText(inviteData.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const input = document.createElement('input');
      input.value = inviteData.invite_code;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleItemClick = (item) => {
    if (item.action === 'navigate') {
      navigate(item.path);
    } else if (item.key === 'username') {
      setShowChangeUsername(true);
    } else if (item.key === 'password') {
      setShowChangePassword(true);
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(isoStr));
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate('/profile')}
        >
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1 className={styles.headerTitle}>我的账户</h1>
      </header>

      <div className={styles.content}>
        {/* 功能菜单 */}
        <div className={styles.menuGrid}>
          {MENU_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={styles.menuCard}
              onClick={() => handleItemClick(item)}
            >
              <div className={`${styles.menuIcon} ${styles[`icon_${item.iconColor}`]}`}>
                <span className="material-symbols-outlined">{item.icon}</span>
              </div>
              <div className={styles.menuBody}>
                <div className={styles.menuTitleRow}>
                  <h3 className={styles.menuTitle}>{item.title}</h3>
                  <span className={`material-symbols-outlined ${styles.menuArrow}`}>chevron_right</span>
                </div>
                <p className={styles.menuDesc}>{item.desc}</p>
              </div>
            </button>
          ))}
        </div>

        {/* PDF 下载额度（终身会员不显示次数，显示「无限」） */}
        <div className={styles.pdfQuotaSection}>
          <div className={styles.pdfQuotaHeader}>
            <div className={`${styles.menuIcon} ${styles.icon_rose}`}>
              <span className="material-symbols-outlined">picture_as_pdf</span>
            </div>
            <div>
              <h3 className={styles.inviteSectionTitle}>PDF 下载额度</h3>
              <p className={styles.inviteSectionDesc}>
                {pdfUnlimited ? '终身会员享受无限下载' : '购买会员可获得更多下载次数'}
              </p>
            </div>
          </div>
          <div className={styles.pdfQuotaBody}>
            <div className={styles.pdfQuotaNumber}>
              <span className={styles.pdfQuotaValue}>
                {pdfUnlimited ? '无限' : (pdfQuota ?? '...')}
              </span>
              {!pdfUnlimited && <span className={styles.pdfQuotaUnit}>次</span>}
            </div>
            {!pdfUnlimited && (
              <p className={styles.pdfQuotaHint}>
                新注册赠送 10 次 · 终身 VIP 用户无限制
              </p>
            )}
          </div>
        </div>

        {/* 邀请记录 */}
        <div className={styles.inviteSection}>
          <div className={styles.inviteHeader}>
            <div className={styles.inviteHeaderLeft}>
              <div className={`${styles.menuIcon} ${styles.icon_purple}`}>
                <span className="material-symbols-outlined">group_add</span>
              </div>
              <div>
                <h3 className={styles.inviteSectionTitle}>邀请好友</h3>
                <p className={styles.inviteSectionDesc}>邀请好友注册，双方各获 30 天 VIP</p>
              </div>
            </div>
          </div>

          {/* 邀请码 */}
          {inviteData?.invite_code && (
            <div className={styles.inviteCodeCard}>
              <div className={styles.inviteCodeLeft}>
                <span className={styles.inviteCodeLabel}>我的邀请码</span>
                <span className={styles.inviteCodeValue}>{inviteData.invite_code}</span>
              </div>
              <button className={styles.copyBtn} onClick={handleCopyCode}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                  {copied ? 'check' : 'content_copy'}
                </span>
                {copied ? '已复制' : '复制'}
              </button>
            </div>
          )}

          {/* 统计 */}
          <div className={styles.inviteStats}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{inviteData?.total_success ?? 0}</span>
              <span className={styles.statLabel}>成功邀请</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{(inviteData?.total_success ?? 0) * 30}</span>
              <span className={styles.statLabel}>累计奖励天数</span>
            </div>
          </div>

          {/* 记录列表 */}
          <div className={styles.inviteList}>
            <h4 className={styles.inviteListTitle}>邀请记录</h4>
            {inviteLoading ? (
              <p className={styles.inviteHint}>加载中…</p>
            ) : !inviteData?.records?.length ? (
              <div className={styles.inviteEmpty}>
                <span className="material-symbols-outlined" style={{ fontSize: 40, opacity: 0.2 }}>person_add</span>
                <p>暂无邀请记录</p>
                <p className={styles.inviteEmptySub}>分享邀请码给好友，双方各获 30 天 VIP</p>
              </div>
            ) : (
              <div className={styles.inviteRecords}>
                {inviteData.records.map((r) => (
                  <div key={r.id} className={styles.inviteRow}>
                    <div className={styles.inviteRowLeft}>
                      <div className={styles.inviteAvatar}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>person</span>
                      </div>
                      <div>
                        <span className={styles.inviteeName}>{r.invitee_username}</span>
                        <span className={styles.inviteTime}>{formatDate(r.created_at)}</span>
                      </div>
                    </div>
                    <div className={styles.inviteRowRight}>
                      <span className={`${styles.inviteStatus} ${styles[`status_${r.status}`]}`}>
                        {STATUS_MAP[r.status] || r.status}
                      </span>
                      {r.status === 'REWARD_ISSUED' && (
                        <span className={styles.rewardTag}>+{r.reward_days}天</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ChangeUsernameModal
        open={showChangeUsername}
        onClose={() => setShowChangeUsername(false)}
      />
      <ChangePasswordModal
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </div>
  );
};

export default ProfileAccount;
